/**
 * Which outcome capabilities moved on this turn, and which way.
 *
 * The outcome capabilities card above shows where everything stands. This shows
 * what the customer's last message actually did to that standing, which is the
 * part that changes what the agent is allowed to say next.
 */
import { Outcome } from "@financial-advisory/data-access/api-types";
import { ArrowRight, MapPin, Target, Users } from "lucide-react";
import { cn } from "@unlikelyai-magic/utils";
import type { OutcomeChange } from "./outcomeChanges";

const ICONS = {
  situation: MapPin,
  segment: Users,
  suggestion: Target,
} as const;

const stateLabel = (outcome: Outcome | null): string => {
  if (outcome === Outcome.TRUE) return "May raise";
  if (outcome === Outcome.FALSE) return "Must not raise";
  return "Not yet known";
};

const isDecided = (outcome: Outcome | null): boolean =>
  outcome === Outcome.TRUE || outcome === Outcome.FALSE;

export const OutcomeChangesList = ({
  changes,
}: {
  changes: OutcomeChange[];
}) => {
  if (changes.length === 0) {
    return (
      <p className="text-[13px] text-brand-muted">
        No outcome changed on this turn.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1.5">
      {changes.map((change) => {
        const Icon = ICONS[change.type];
        const wentBack = !isDecided(change.newOutcome);
        return (
          <li
            key={`${change.type}-${change.productTreeId}`}
            className={cn(
              "flex items-center gap-2 rounded-md border px-2.5 py-1.5",
              wentBack
                ? "border-amber-500/30 bg-amber-500/[0.08]"
                : change.newOutcome === Outcome.TRUE
                  ? "border-green-500/30 bg-green-500/[0.05]"
                  : "border-red-500/25 bg-red-500/[0.04]"
            )}
          >
            <Icon
              className="h-3.5 w-3.5 flex-none text-brand-muted"
              aria-hidden={true}
            />
            <span className="min-w-0 flex-1 truncate text-xs">
              {change.name}
            </span>
            <span className="flex flex-none items-center gap-1.5 text-[11px]">
              <span className="text-brand-muted">
                {stateLabel(change.previousOutcome)}
              </span>
              <ArrowRight
                className="h-3 w-3 text-brand-muted"
                aria-hidden={true}
              />
              <span
                className={cn(
                  "font-semibold",
                  wentBack
                    ? "text-amber-700"
                    : change.newOutcome === Outcome.TRUE
                      ? "text-green-600"
                      : "text-red-600"
                )}
              >
                {stateLabel(change.newOutcome)}
              </span>
            </span>
          </li>
        );
      })}
    </ul>
  );
};
